import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Leaf, Eye } from 'lucide-react';
import { StyleSummary, formatGbp, priceFrom, swatchFromRgb, cleanText, cleanRange, cleanBrand } from '../../../lib/clothingBrowseApi';

const GREEN = '#78BE20';
const MAX_SWATCHES = 6;

interface ProductCardProps {
  style: StyleSummary;
  active: boolean;
  onQuickLook: () => void;
}

const ProductCard: React.FC<ProductCardProps> = ({ style, active, onQuickLook }) => {
  const [hoverImage, setHoverImage] = useState<string | null>(null);
  const variants = style.color_variants || [];
  const price = priceFrom(style);
  const image = hoverImage || style.primary_product_image_url || '';
  const eco = style.sustainable_organic === 'Yes';
  const detailsTo = `/products/${encodeURIComponent(style.style_code)}`;

  return (
    <article
      className={`group relative flex flex-col rounded-2xl bg-white transition-shadow duration-300 ${
        active ? 'shadow-[0_0_0_2px_#78BE20]' : 'hover:shadow-[0_18px_40px_-20px_rgba(0,0,0,0.35)]'
      }`}
    >
      <div className="relative">
      <Link to={detailsTo} className="relative block aspect-[4/5] overflow-hidden rounded-t-2xl bg-[#f3f4f6]" aria-label={`${style.style_name} details`}>
        {image ? (
          <img
            src={image}
            alt={cleanText(style.style_name)}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-contain p-4 mix-blend-multiply transition-transform duration-500 ease-out group-hover:scale-[1.04]"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-black/30 font-poppins text-xs">No image</div>
        )}

        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          {eco && (
            <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-white/90 backdrop-blur text-[10px] font-semibold uppercase tracking-[0.08em]" style={{ color: '#2f7a12' }}>
              <Leaf className="w-3 h-3" /> Eco
            </span>
          )}
        </div>
      </Link>

      <button
        type="button"
        onClick={onQuickLook}
        className={`absolute left-3 right-3 bottom-3 h-10 bg-black text-white font-poppins text-[12px] font-semibold tracking-[0.1em] uppercase inline-flex items-center justify-center gap-2 transition-all duration-300 hover:bg-[#78BE20] ${
          active ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 focus-visible:opacity-100'
        }`}
        aria-expanded={active}
      >
        <Eye className="w-4 h-4" />
        {active ? 'Close' : 'Quick look'}
      </button>
      </div>

      <div className="flex flex-col gap-1.5 px-4 pt-3 pb-4">
        <div className="flex items-baseline justify-between gap-2">
          <span className="font-poppins text-[11px] font-semibold tracking-[0.12em] uppercase text-black/50 truncate">{cleanBrand(style.brand)}</span>
          <span className="font-poppins text-[11px] text-black/35 tabular-nums shrink-0">{style.style_code}</span>
        </div>
        <Link to={detailsTo} className="font-montserrat font-bold text-[15px] leading-snug text-black line-clamp-2 hover:underline decoration-[#78BE20] decoration-2 underline-offset-2">
          {cleanText(style.style_name)}
        </Link>
        <div className="flex items-end justify-between gap-2 mt-1">
          <div>
            {price !== null ? (
              <p className="font-poppins text-[14px]">
                <span className="text-black/50 text-[12px]">from </span>
                <span className="font-semibold">{formatGbp(price)}</span>
              </p>
            ) : (
              <p className="font-poppins text-[12px] text-black/50">Price on enquiry</p>
            )}
            {style.size_range && <p className="font-poppins text-[11px] text-black/50 mt-0.5">{cleanRange(style.size_range)}</p>}
          </div>

          {variants.length > 0 && (
            <ul className="flex items-center -space-x-1" aria-label={`${variants.length} colours`} onMouseLeave={() => setHoverImage(null)}>
              {variants.slice(0, MAX_SWATCHES).map((v) => (
                <li
                  key={v.colourCode}
                  title={v.colourName}
                  onMouseEnter={() => v.colourImage && setHoverImage(v.colourImage)}
                  className="w-4 h-4 rounded-full border border-white shadow-[0_0_0_1px_rgba(0,0,0,0.15)] hover:z-10 hover:scale-125 transition-transform"
                  style={{ background: swatchFromRgb(v.rgb) }}
                />
              ))}
              {variants.length > MAX_SWATCHES && (
                <li className="pl-2 font-poppins text-[11px] text-black/50 tabular-nums">+{variants.length - MAX_SWATCHES}</li>
              )}
            </ul>
          )}
        </div>
      </div>
      <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-b-2xl scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" style={{ backgroundColor: GREEN }} aria-hidden />
    </article>
  );
};

export default ProductCard;
